import { useCallback, useEffect, useMemo, useState } from 'react'
import { sileo } from 'sileo'
import { useApp } from '../context/AppContext'
import incidentService from '../services/incidentService'
import { currentUserName } from './useIncidents'

/**
 * Registro de un incidente reportado por un colaborador, común a los dos
 * modos de /reportar-incidente: catálogo de activos, autor y envío.
 *
 * @returns {{
 *   assets: Array<Object>,
 *   author: string,
 *   saving: boolean,
 *   created: Object|null,
 *   submit: (payload: Object, assetIds: number[]) => Promise<boolean>,
 *   clear: () => void
 * }}
 */
export default function useReportIncident() {
  const { employee, user } = useApp()
  const [assets, setAssets] = useState([])
  const [saving, setSaving] = useState(false)
  /** Incidente recién creado; al existir se muestra la confirmación. */
  const [created, setCreated] = useState(null)

  const author = useMemo(
    () => currentUserName(employee, user),
    [employee, user]
  )

  useEffect(() => {
    let active = true
    incidentService.getAssets().then(({ success, data }) => {
      if (active && success) setAssets(data)
    })
    return () => {
      active = false
    }
  }, [])

  const submit = useCallback(
    async (payload, assetIds = []) => {
      setSaving(true)
      const { success, data, error } = await incidentService.create(
        { ...payload, CreatedBy: author },
        assetIds
      )
      setSaving(false)

      if (!success) {
        sileo.error({
          title: 'No se pudo enviar el reporte',
          description: error,
        })
        return false
      }
      setCreated(data)
      sileo.success({ title: `Reporte ${data.Code} enviado` })
      return true
    },
    [author]
  )

  const clear = useCallback(() => setCreated(null), [])

  return { assets, author, saving, created, submit, clear }
}
